import { Contact } from '../../../api/contacts/types'
import { DataForExportApi, DataForExportItem, ExportListParams } from '../../../api/exportData/types'
import { getQueryData } from '../../../api/helpers'
import { ProjectItem } from '../../../api/projects/types'
import { Property } from '../../../api/properties/types'

export const findKeysInobject = (obj: Record<string, string | undefined>, searchKey: string, parentKey = ''): string[] => {
    let result: string[] = []

    for (const key in obj) {
        const fullKey = parentKey ? `${parentKey}.${key}` : key

        if (key.includes(searchKey)) {
            result.push(fullKey)
        }

        const value = obj[key]
        if (value && typeof value === 'object' && !Array.isArray(value)) {
            result = result.concat(findKeysInobject(value, fullKey))
        }
    }

    return result
}

export const transformData = (raw: { [key: string]: string | undefined }, mapping: Record<string, string>): Record<string, string | undefined> => {
    const transformed: Record<string, string | undefined> = {}

    const alwaysInclude = findKeysInobject(mapping, '*')
    const findAgentArray = findKeysInobject(raw, 'agent_0')

    const joinMatchingFields = (prefix: string, data: Record<string, string | undefined>) =>
        Object.keys(data)
            .filter((key) => key.startsWith(prefix) && data[key])
            .map((key) => data[key])
            .join(' ')

    const joinNameFields = (prefix: string, data: Record<string, string | undefined>) =>
        [`${prefix}firstName`, `${prefix}lastName`]
            .map((key) => data[key])
            .filter(Boolean)
            .join(' ')

    if (mapping.agent) transformed[mapping.agent] = joinMatchingFields(findAgentArray.length > 0 ? 'agent_0' : 'agent_', raw)
    if (mapping.primaryAgent) transformed[mapping.primaryAgent] = joinNameFields('primaryAgent_', raw)
    if (mapping.contactPerson) transformed[mapping.contactPerson] = joinMatchingFields('contactPerson_', raw)
    if (mapping.owner) transformed[mapping.owner] = joinMatchingFields('owner_0_contactPerson', raw)

    for (const [key, label] of Object.entries(mapping)) {
        if (key === 'agent' || key === 'primaryAgent') continue

        if (key.includes('*')) {
            const regex = new RegExp('^' + key.replace('*', '\\d+') + '$')
            const matches = Object.entries(raw)
                .filter(([rawKey, val]) => regex.test(rawKey) && val)
                .map(([_, val]) => val)

            if (matches.length > 0) {
                transformed[label] = matches.join(', ')
            } else if (alwaysInclude.includes(key)) {
                transformed[label] = ''
            }

            continue
        }

        if (Object.prototype.hasOwnProperty.call(raw, `${key}_name`)) {
            transformed[label] = raw[`${key}_name`]
        } else if (Object.prototype.hasOwnProperty.call(raw, key)) {
            transformed[label] = raw[key]
        }
    }

    return transformed
}

export const findMatchingKeys = (item: DataForExportItem, baseKey: string): string[] => {
    const [prefix, suffix] = baseKey.split(/_\d+_/)
    const regex = new RegExp(`^${prefix}_\\d+_${suffix}$`)

    return item ? Object.keys(item).filter((key) => regex.test(key)) : ['']
}

export const flattenObject = (obj: DataForExportItem, prefix = '', res: any = {}) => {
    for (const key in obj) {
        const value: Record<string, string> = obj[key as keyof DataForExportItem]
        const newKey = prefix ? `${prefix}_${key}` : key

        if (Array.isArray(value)) {
            if (value.length > 0 && typeof value[0] === 'object') {
                value.forEach((item, index) => {
                    flattenObject(item, `${newKey}_${index}`, res)
                })
            } else {
                res[newKey] = value.join(', ')
            }
        } else if (typeof value === 'object' && value !== null) {
            flattenObject(value as unknown as DataForExportItem, newKey, res)
        } else {
            res[newKey] = value
        }
    }
    return res
}

export const getSelectedItems = (selectedRowsIds: (number | string)[], pathname: string, params: ExportListParams): DataForExportItem[] => {
    const module = pathname.split('/')[1]
    const cacheModule = module === 'companies' ? 'contacts' : module
    const cachedData = getQueryData<DataForExportApi>([cacheModule, 'list'], params)

    if (cachedData?.items) {
        const items = cachedData.items as (ProjectItem | Property | Contact)[]
        return items.filter((item) => item?.id !== null && selectedRowsIds.includes(item.id))
    }

    console.warn(`No cached data found for: ${pathname}`)
    return []
}
